import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Description as FormsIcon,
  Assignment as SubmissionsIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSnackbar } from '../contexts/SnackbarContext';

interface DashboardStats {
  totalForms: number;
  totalSubmissions: number;
  activeForms: number;
}

interface NotificationService {
  service: string;
  status: string;
  provider: string;
  lastCheck: string;
  configuration?: any;
}

interface NotificationStatus {
  services: NotificationService[];
  overall: {
    status: string;
    timestamp: string;
  };
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [notificationStatus, setNotificationStatus] = useState<NotificationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setLoading(true);
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const token = localStorage.getItem('token');
        
        if (!token) {
          showSnackbar('Please log in to access dashboard', 'error');
          navigate('/login');
          return;
        }
        
        // Fetch forms and submissions data
        const [formsResponse, submissionsResponse, notificationResponse] = await Promise.all([
          axios.get(`${API_URL}/api/forms`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/submissions?limit=5`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/submissions/notification-status`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => null), // Don't fail if notification status is unavailable
        ]);

        const forms = formsResponse.data.forms || [];

        setStats({
          totalForms: forms.length,
          activeForms: forms.filter((form: any) => form.isActive).length,
          totalSubmissions: submissionsResponse.data.total || 0,
        });

        // Set notification status if available
        if (notificationResponse?.data?.data) {
          setNotificationStatus(notificationResponse.data.data);
        }
      } catch (error: any) {
        console.error('Error fetching dashboard stats:', error);
        
        if (error.response?.status === 401) {
          showSnackbar('Authentication failed. Please log in again.', 'error');
          localStorage.removeItem('token');
          navigate('/login');
        } else if (error.response?.status === 403) {
          showSnackbar('Access denied. Admin privileges required.', 'error');
        } else if (error.response?.status === 500) {
          showSnackbar('Server error. Please try again later.', 'error');
        } else if (error.code === 'NETWORK_ERROR' || !error.response) {
          showSnackbar('Network error. Please check your connection.', 'error');
        } else {
          showSnackbar('Error loading dashboard data', 'error');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, [showSnackbar, navigate]);

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
    action?: () => void;
  }> = ({ title, value, icon, color, action }) => (
    <Card elevation={3} sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h3" component="div" color={color}>
              {value}
            </Typography>
          </Box>
          <Box sx={{            color: color,
            fontSize: 40
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
      {action && (
        <CardActions>
          <Button size="small" onClick={action}>
            View Details
          </Button>
        </CardActions>
      )}
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        Welcome to your form builder admin panel. Here's an overview of your forms and submissions.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
          <StatCard
            title="Total Forms"
            value={stats?.totalForms || 0}
            icon={<FormsIcon />}
            color="primary.main"
            action={() => navigate('/admin/forms')}
          />
        </Box>
        <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
          <StatCard
            title="Active Forms"
            value={stats?.activeForms || 0}
            icon={<TrendingUpIcon />}
            color="success.main"
            action={() => navigate('/admin/forms')}
          />
        </Box>
        <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
          <StatCard
            title="Total Submissions"
            value={stats?.totalSubmissions || 0}
            icon={<SubmissionsIcon />}
            color="info.main"
            action={() => navigate('/admin/submissions')}
          />
        </Box>
        <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
          <StatCard
            title="Response Rate"
            value={Math.round((stats?.totalSubmissions || 0) / Math.max(stats?.totalForms || 1, 1) * 100)}
            icon={<PeopleIcon />}
            color="warning.main"
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ flex: '1 1 400px', minWidth: '300px' }}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FormsIcon />}
                onClick={() => navigate('/admin/forms/new')}
                fullWidth
              >
                Create New Form
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<SubmissionsIcon />}
                onClick={() => navigate('/admin/submissions')}
                fullWidth
              >
                View All Submissions
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<FormsIcon />}
                onClick={() => navigate('/admin/forms')}
                fullWidth
              >
                Manage Forms
              </Button>
              <Button
                variant="outlined"
                color="info"
                startIcon={<SettingsIcon />}
                onClick={() => navigate('/admin/settings')}
                fullWidth
              >
                Settings
              </Button>
            </Box>
          </Paper>
        </Box>

        <Box sx={{ flex: '1 1 400px', minWidth: '300px' }}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              System Status
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {notificationStatus ? (
                notificationStatus.services.map((service) => {
                  const getStatusColor = (status: string) => {
                    switch (status) {
                      case 'active': return 'success.main';
                      case 'setup_required': return 'warning.main';
                      default: return 'error.main';
                    }
                  };

                  const getStatusText = (status: string) => {
                    switch (status) {
                      case 'active': return 'Active';
                      case 'setup_required': return 'Setup Required';
                      default: return 'Error';
                    }
                  };

                  return (
                    <Box key={service.service} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2">{service.service} </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getStatusColor(service.status) }} />
                        <Typography variant="body2" color={getStatusColor(service.status)}>
                          {getStatusText(service.status)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2">SMS Notifications</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                      <Typography variant="body2" color="success.main">Active</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Email Notifications</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                      <Typography variant="body2" color="success.main">Active</Typography>
                    </Box>
                  </Box>
                </>
              )}
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate('/admin/settings')}
                fullWidth
                size="small"
              >
                Configure Settings
              </Button>
            </Box>
          </Paper>
        </Box>

        <Box sx={{ flex: '1 1 400px', minWidth: '300px' }}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Getting Started
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Welcome to your form builder! Create dynamic forms with custom fields, payment integration, and export capabilities.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={() => navigate('/admin/forms/new')}
                fullWidth
              >
                Create Your First Form
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
